/**
 * useAvailability — fetches real availability from the API on mount.
 *
 * Returns:
 *   blockedDates: Date[] — fully blocked dates for the date picker filterDate prop
 *   dateDetails:  Record<string, DateAvailability> — per-date spot breakdown
 *   loading:      boolean
 *   error:        string | null
 *   refetch:      () => void — call after a successful booking to refresh
 */

import { useCallback, useEffect, useState } from 'react';
import { AvailabilityResponse, DateAvailability, fetchAvailability } from '../utils/apiService';

interface UseAvailabilityResult {
  blockedDates: Date[];
  dateDetails: Record<string, DateAvailability>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function isoToDate(iso: string): Date {
  // Parse "YYYY-MM-DD" in local time (avoid UTC shift)
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function useAvailability(): UseAvailabilityResult {
  const [data, setData] = useState<AvailabilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchAvailability()
      .then(res => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) {
          // Non-fatal: fall back to no blocked dates if API is unreachable
          setError('Could not load availability. All dates shown as available.');
          setData({ blocked_dates: [], date_details: {} });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tick]);

  const refetch = useCallback(() => setTick(t => t + 1), []);

  const blockedDates = (data?.blocked_dates ?? []).map(isoToDate);
  const dateDetails = data?.date_details ?? {};

  return { blockedDates, dateDetails, loading, error, refetch };
}
